import React, { useState, useEffect } from 'react';
import OrderDesign from '../OrderDesign';
import './CounterStrikeDialog.css';

/**
 * Counter Strike Dialog Component
 * Displays when a Counter Strike order is drawn from a dismantled barrier
 * Shows order in extended view with "We need to strike back" text
 * Provides "Strike!" and "Keep" buttons
 * When "Strike!" is clicked, allows direct zone targeting like carousel
 * 
 * Mobile support: When isMobile is true, displays with smaller card size
 * to fit the 932x430 mobile viewport.
 */
const CounterStrikeDialog = ({ orders, onSelectZone, onKeepOrder, playerId, onStrikeModeActivated, isMobile = false, processingCounterStrikeId = null, handSize = 0, gameState = null }) => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [strikeMode, setStrikeMode] = useState(false);
  

  // Initialize selected order from incoming orders
  // ✅ FIX: Only show orders that aren't currently being processed
  // ✅ FIX: When orders array changes (e.g., after playing one counterstrike), always update selectedOrder
  // to the first order in the array that isn't being processed, and reset strikeMode to show the dialog again
  useEffect(() => {
    if (orders && orders.length > 0) {
      // ✅ FIX: Filter out orders that are currently being processed
      const availableOrders = processingCounterStrikeId 
        ? orders.filter(o => o.instanceId !== processingCounterStrikeId)
        : orders;
      
      if (availableOrders.length > 0) {
        // Always set to first available order when orders change
        const firstOrder = availableOrders[0];
        // Only update if the selected order is different or doesn't exist
        setSelectedOrder(prev => {
          // If previous order is not in the available orders array, switch to first order
          if (!prev || !availableOrders.find(o => o.instanceId === prev.instanceId)) {
            return firstOrder;
          }
          // Otherwise keep the same order if it's still in the array
          return prev;
        });
        // Always reset strikeMode when orders change to allow dialog to show for subsequent counterstrikes
        setStrikeMode(false);
      } else {
        // All orders are being processed - hide dialog but keep selectedOrder
        setStrikeMode(false);
      }
    } else {
      setSelectedOrder(null);
      setStrikeMode(false);
    }
  }, [orders, processingCounterStrikeId]);

  // Note: No timeout for human players - they should make their own decisions
  // Backend timeout protection only applies to bots

  const handleStrike = () => {
    if (selectedOrder) {
      setStrikeMode(true);
      // Use the same mechanism as carousel - set selectedOrderInstanceId
      if (onStrikeModeActivated) {
        onStrikeModeActivated(selectedOrder);
      }
      // ✅ FIX: Clear selectedOrder immediately to prevent dialog from showing it while processing
      // The order will be filtered out by processingCounterStrikeId check anyway, but this ensures clean state
      setSelectedOrder(null);
    }
  };

  const handleKeep = () => {
    if (selectedOrder) {
      onKeepOrder(selectedOrder.id, selectedOrder.instanceId);
      // ✅ FIX: Clear selectedOrder immediately to prevent dialog from showing it while processing
      setSelectedOrder(null);
    }
  };

  // Early returns must come AFTER hooks to satisfy React rules of hooks
  if (!orders || orders.length === 0) {
    return null;
  }
  
  // ✅ FIX: Don't show dialog if all orders are being processed
  const availableOrders = processingCounterStrikeId 
    ? orders.filter(o => o.instanceId !== processingCounterStrikeId)
    : orders;
  
  if (availableOrders.length === 0) {
    return null;
  }

  // Hide dialog when in strike mode to allow zone clicking
  if (strikeMode) {
    return null;
  }

  return (
    <div className="counter-strike-overlay" data-no-battle-flashlight="true">
      <div 
        className="counter-strike-dialog"
        style={isMobile ? {
          transform: 'scale(0.45)',
          transformOrigin: 'center center'
        } : {}}
      >
        <div className="counter-strike-header">
          <h2 style={isMobile ? { fontSize: '32px' } : {}}>We need to strike back</h2>
        </div>

        <div className="counter-strike-order-display">
          {selectedOrder && (
            <div className="counter-strike-order-card">
              <OrderDesign
                order={selectedOrder}
                count={0}
                minimal={false}
                isOOR={false}
                isSelected={false}
                isTarget={false}
                isCounterStrike={true}
                gameState={gameState}
                userId={playerId}
              />
            </div>
          )}
        </div>

        <div className="counter-strike-action-buttons">
          <button
            className="counter-strike-strike-button"
            onClick={handleStrike}
            style={isMobile ? { padding: '16px 32px', fontSize: '24px' } : {}}
          >
            Strike!
          </button>
          {handSize < 10 && (
            <button
              className="counter-strike-keep-button"
              onClick={handleKeep}
              style={isMobile ? { padding: '16px 32px', fontSize: '24px' } : {}}
              title="Add to hand"
            >
              Keep
            </button>
          )}
          {handSize >= 10 && (
            <div style={{ 
              fontSize: isMobile ? '16px' : '14px', 
              color: '#ff6b6b', 
              marginTop: '8px',
              textAlign: 'center'
            }}>
              Hand is full (10/10) - You can only Strike!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CounterStrikeDialog;

